import { useDispatch } from "react-redux";
import { userSlice } from "@/redux/User";
import { axiosGet } from "@/services/axiosService";
import Papa from "papaparse";
import MiniTooltip from "@/assets/MiniTooltip";

type Props = {
  selectedBot: any;
  selectedFilter?: string;
  searchString?: string;
  fromDate?: any;
  toDate?: any;
  selectedCategory?: any;
  sort?: any;
};

export default function DownloadCSVButton({
  selectedBot,
  selectedFilter,
  searchString,
  fromDate,
  toDate,
  selectedCategory,
  sort,
}: Props) {
  const dispatch = useDispatch();

  const downloadCSV = async () => {
    try {
      const botCustomId = selectedBot?.botCustomId || "";
      const querySearchString = encodeURIComponent(searchString || "");

      const fetchPage = async (pageNumber: number) => {
        const params = new URLSearchParams({
          botCustomId,
          selectedFilter: selectedFilter || "all",
          searchString: querySearchString,
          page: pageNumber.toString(),
          pageSize: "50",
        });

        if (fromDate) params.append("fromDate", fromDate.toString());
        if (toDate) params.append("toDate", toDate.toString());
        const categoryParam = Array.isArray(selectedCategory)
          ? selectedCategory.join(",")
          : selectedCategory;
        if (categoryParam) params.append("category", categoryParam.toString());
        if (sort) params.append("sort", sort.toString());

        const response = await axiosGet(
          `/analysis/allQuestionsData?${params.toString()}`,
        );
        return response.data;
      };

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: "Preparing download... This may take a while.",
        }),
      );

      const firstPageData = await fetchPage(1);
      if (
        !firstPageData?.data?.questions ||
        firstPageData.data.questions.length === 0
      ) {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "error",
            message: "No data available to download",
          }),
        );
        return;
      }

      let allQuestions = [...firstPageData.data.questions];
      const totalPages = firstPageData.data.pagination?.totalPages || 1;

      for (let i = 2; i <= totalPages; i++) {
        const pageData = await fetchPage(i);
        if (pageData?.data?.questions) {
          allQuestions = [...allQuestions, ...pageData.data.questions];
        }
      }

      const csvData = allQuestions.map((q: any, index: number) => ({
        "SR. No.": index + 1,
        Question: q.query,
        "Agent Answer": q.answer,
        Categories: Array.isArray(q.categories)
          ? q.categories.join(", ")
          : q.categories || "-",
        "Positive Feedback": q.positive,
        "Negative Feedback": q.negative,
        Count: q.count,
      }));

      const csv = Papa.unparse(csvData);
      const csvDataPrepared = new Blob([csv], {
        type: "text/csv;charset=utf-8",
      });
      const fileURL = window.URL.createObjectURL(csvDataPrepared);

      const tempLink = document.createElement("a");
      tempLink.href = fileURL;
      tempLink.setAttribute("download", `${selectedFilter || "questions"}.csv`);
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: "CSV Downloaded Successfully!",
        }),
      );
    } catch (error) {
      console.error("Error downloading CSV", error);
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Failed to download CSV",
        }),
      );
    }
  };

  return (
    <button
      onClick={downloadCSV}
      className="flex cursor-pointer items-center justify-center rounded-[10px] border border-[rgba(0,0,0,0.10)] bg-white p-[12px] text-[#444444] transition-colors hover:bg-gray-50"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7 10L12 15L17 10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 15V3"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <MiniTooltip text="Download CSV" position="bottom" />
    </button>
  );
}
